import { Link, useParams } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import ArticleCard from "@/components/feature/ArticleCard";
import { SITE_INFO, categories, comics, articles } from "@/mocks/data";

const CATEGORY_BANNERS: Record<string, string> = {
  reqiu: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20adventure%20comics%2C%20heroes%20in%20dynamic%20battle%2C%20warm%20red%20and%20orange%20flames%2C%20dark%20gradient%20overlay%20for%20text%2C%20epic%20action&width=1600&height=500&seq=cat-reqiu-banner&orientation=landscape",
  lianai: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20romance%20comics%2C%20cherry%20blossoms%20and%20hearts%2C%20soft%20pink%20warm%20tones%2C%20dreamy%20atmosphere%2C%20dark%20gradient%20for%20text&width=1600&height=500&seq=cat-lianai-banner&orientation=landscape",
  xuanhuan: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20xianxia%20fantasy%20comics%2C%20flying%20swordsman%20among%20clouds%2C%20gold%20and%20jade%20tones%2C%20epic%20mystical%2C%20dark%20gradient%20for%20text&width=1600&height=500&seq=cat-xuanhuan-banner&orientation=landscape",
  xuanyi: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20mystery%20comics%2C%20foggy%20city%20detective%20silhouettes%2C%20warm%20amber%20noir%20tones%2C%20dark%20gradient%20for%20text&width=1600&height=500&seq=cat-xuanyi-banner&orientation=landscape",
  gaoxiao: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20comedy%20comics%2C%20funny%20cartoon%20characters%20laughing%2C%20cheerful%20warm%20colors%2C%20cute%20style%2C%20dark%20gradient%20for%20text&width=1600&height=500&seq=cat-gaoxiao-banner&orientation=landscape",
  kehuan: "https://readdy.ai/api/search-image?query=manga%20illustration%20banner%20for%20sci-fi%20comics%2C%20space%20ships%20and%20nebula%2C%20warm%20golden%20star%20light%2C%20cinematic%2C%20dark%20gradient%20for%20text&width=1600&height=500&seq=cat-kehuan-banner&orientation=landscape",
};

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = categories.find((c) => c.id === categoryId) || categories[0];

  const categoryComics = comics.filter((c) => c.categoryId === category.id);
  const categoryArticles = articles.filter((a) => a.categoryId === category.id);
  const categoryBanner = CATEGORY_BANNERS[category.id] || CATEGORY_BANNERS.reqiu;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name}漫画 - 漫画岛`,
      url: `${SITE_INFO.domain}/category/${category.id}`,
      description: `漫画岛${category.name}漫画专题，收录${category.name}类型的热门漫画与书评讨论。${category.desc}。`,
      isPartOf: { "@type": "WebSite", name: SITE_INFO.name, url: SITE_INFO.domain },
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_INFO.domain}/` },
        { "@type": "ListItem", position: 2, name: "分类书库", item: `${SITE_INFO.domain}/category/${category.id}` },
        { "@type": "ListItem", position: 3, name: category.name, item: `${SITE_INFO.domain}/category/${category.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${category.name}漫画 - 漫画书评与同好讨论`}
        description={`漫画岛${category.name}漫画专题：${category.desc}。收录${category.name}类型热门漫画安利、深度书评与同好讨论，看完漫画来聊剧情、求推荐，找同好一起追更。`}
        keywords={`漫画岛,${category.name}漫画,${category.name}漫画推荐,${category.name}书评,漫画同好讨论`}
        canonicalPath={`/category/${category.id}`}
        jsonLd={jsonLd}
      />

      {/* 分类 Banner */}
      <section className="relative w-full overflow-hidden" aria-label={`${category.name}分类介绍`}>
        <div className="absolute inset-0">
          <img src={categoryBanner} alt={`${category.name}漫画分类背景`} title={`${category.name}漫画`} className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/40"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <Breadcrumb items={[{ label: "分类书库" }, { label: category.name }]} />
          <h1 className="mt-6 font-heading text-3xl font-black text-background-50 md:text-4xl">{category.name}漫画</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">{category.desc}</p>
        </div>
      </section>

      {/* 分类切换 */}
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <nav aria-label="分类切换" className="mt-6">
          <div className="inline-flex max-w-full flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  c.id === category.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${c.icon} mr-1.5`} aria-hidden="true"></i>
                {c.name}
              </Link>
            ))}
          </div>
        </nav>
      </div>

      {/* 该分类漫画 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6" aria-label={`${category.name}漫画列表`}>
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">本类型热门漫画</h2>
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">{category.count}部</span>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categoryComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* 该分类书评 */}
      <section className="bg-background-100" aria-label={`${category.name}书评讨论`}>
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">{category.name}书评 · 同好讨论</h2>
          <p className="mt-2 text-sm text-foreground-600">看完漫画来聊剧情、求推荐，找同好一起追更</p>
          {categoryArticles.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
              <i className="ri-article-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-3 text-sm text-foreground-500">该类型书评正在路上，敬请期待</p>
              <Link to="/" className="mt-3 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600">
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}